import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Shield, ArrowRight, Sparkles } from 'lucide-react';

/**
 * SecuritySuitePromoModal — one-shot popup that surfaces the new AI Security
 * Suite the first time a user lands on /tools. Dismissal is stored in
 * localStorage so it never bugs the same browser twice. Bypassable via URL
 * `?promo=1` for demoing.
 */
const STORAGE_KEY = 'eh_security_suite_promo_seen_v1';

const HIGHLIGHTS = [
  { color: '#ff2d92', label: 'Scam Detector' },
  { color: '#c084fc', label: 'Fake Profile Checker' },
  { color: '#25d366', label: 'WhatsApp Guard' },
  { color: '#a78bfa', label: 'Mobile Security' },
  { color: '#ff7a3d', label: 'Hacked Account Checker' },
  { color: '#4de0ff', label: 'Privacy Leak Scanner' },
  { color: '#ffd34d', label: 'Website Scam Check' },
  { color: '#f472b6', label: 'Cyberbullying Shield' },
  { color: '#00ff9d', label: 'Safety Assistant' },
];

const SecuritySuitePromoModal = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const params = new URLSearchParams(window.location.search);
      const force = params.get('promo') === '1';
      const seen = window.localStorage.getItem(STORAGE_KEY);
      if (force || !seen) {
        // Small delay so it feels intentional, not intrusive
        const t = setTimeout(() => setOpen(true), 900);
        return () => clearTimeout(t);
      }
    } catch { /* localStorage disabled — silent noop */ }
  }, []);

  const dismiss = () => {
    setOpen(false);
    try { window.localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center px-4 py-8 backdrop-blur-md"
      style={{ background: 'rgba(0,0,0,.72)' }}
      onClick={dismiss}
      data-testid="security-suite-promo"
    >
      <div
        className="relative w-full max-w-lg rounded-2xl border-2 overflow-hidden"
        style={{
          borderColor: '#ff2d92aa',
          background: 'linear-gradient(155deg, rgba(255,45,146,.14) 0%, rgba(77,224,255,.10) 45%, rgba(0,255,157,.10) 100%), #0b0d10',
          boxShadow: '0 24px 80px -20px rgba(255,45,146,.45)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top prism bar */}
        <span aria-hidden className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: 'linear-gradient(90deg, #ff2d92, #4de0ff, #00ff9d, #ffd34d, #ff2d92)' }} />

        {/* Close */}
        <button
          onClick={dismiss}
          data-testid="security-suite-promo-close"
          aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-full border border-[var(--eh-border)] hover:border-[var(--eh-red)] hover:text-[var(--eh-red)] transition-colors"
        >
          <X size={14} />
        </button>

        <div className="p-6 sm:p-7">
          {/* Header pill */}
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full mb-4" style={{ background: 'rgba(255,45,146,.16)', border: '1px solid #ff2d9255' }}>
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: '#ff2d92' }} />
              <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: '#ff2d92' }} />
            </span>
            <span className="eh-mono text-[10px] tracking-[0.25em] font-bold" style={{ color: '#ff2d92' }}>NEW · SHIPPED TODAY</span>
          </div>

          <div className="flex items-start gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: 'rgba(255,45,146,.14)', border: '1px solid #ff2d9255', color: '#ff2d92' }}>
              <Shield size={22} strokeWidth={1.8} />
            </div>
            <div>
              <h2 className="font-black text-xl sm:text-2xl leading-tight" style={{ fontFamily: 'Inter,sans-serif' }}>
                AI Security Suite — <span className="eh-neon">9 tools</span>
              </h2>
              <div className="text-[13px] opacity-80 mt-1 leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
                Paste a message · a URL · a chat screenshot · or list your phone symptoms — AI tells you if it&apos;s dangerous, how bad, and exactly what to do next.
              </div>
            </div>
          </div>

          {/* Tool chips */}
          <div className="flex flex-wrap gap-1.5 mb-5" data-testid="security-suite-promo-chips">
            {HIGHLIGHTS.map((h) => (
              <span
                key={h.label}
                className="eh-mono text-[10px] tracking-widest font-bold px-2 py-1 rounded"
                style={{ background: `${h.color}18`, color: h.color, border: `1px solid ${h.color}44` }}
              >
                {h.label}
              </span>
            ))}
          </div>

          {/* Feature line */}
          <div className="rounded-lg p-3 mb-5 border" style={{ background: 'rgba(255,211,77,.06)', borderColor: 'rgba(255,211,77,.28)' }}>
            <div className="eh-mono text-[10px] tracking-widest font-bold mb-1" style={{ color: '#ffd34d' }}>// HOW IT WORKS</div>
            <div className="text-[12.5px] opacity-90 leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
              3 free scans / day · every tool. When you hit the limit, ₹5 per scan from your wallet. <Sparkles size={11} className="inline text-[#ffd34d]" /> Operative Pass = unlimited.
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-2.5">
            <Link
              to="/tools/security"
              onClick={dismiss}
              data-testid="security-suite-promo-cta"
              className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-md font-bold text-sm tracking-wider"
              style={{ background: '#ff2d92', color: '#000' }}
            >
              CHECK IT NOW <ArrowRight size={14} />
            </Link>
            <button
              onClick={dismiss}
              data-testid="security-suite-promo-later"
              className="px-4 py-3 rounded-md text-xs tracking-widest eh-mono border border-[var(--eh-border)] opacity-70 hover:opacity-100"
            >
              MAYBE LATER
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecuritySuitePromoModal;
